import { Printer } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";

const SECTIONS = [
  {
    title: "Gather as you go",
    items: [
      "Keep income records together in one folder (pay stubs, 1099s, business income)",
      "Track deductible expenses when they happen, not in April",
      "Note charitable contributions and receipts as you make them",
      "Keep records of estimated tax payments you make",
      "Save records of major purchases, sales, or refinances",
    ],
  },
  {
    title: "Note life changes during the year",
    items: [
      "Marriage, divorce, or a change in filing status",
      "A new child or a dependent who aged out",
      "A move. New city, new state, remote work in another state",
      "A job change, a business started or closed",
      "Retirement accounts opened, contributed to, or withdrawn",
    ],
  },
  {
    title: "Before preparation time",
    items: [
      "Collect income forms: W-2s, 1099s, interest and dividend statements",
      "List self-employment income and related expenses, if any",
      "Summarize estimated payments made during the year",
      "Gather deduction paperwork you noted during the year",
      "Write down your questions while they’re fresh",
    ],
  },
];

export default function ChecklistPersonal() {
  return (
    <>
      <Seo
        title="Personal Tax Preparation Checklist (Printable) | Directive Optive Solutions"
        description="A free printable personal tax checklist: what to gather through the year so tax preparation is organized and calm. Income forms, life changes, and readiness."
        path="/resources/personal-tax-checklist"
      />
      <PageHeader
        eyebrow="Free resource. No email required"
        title="Personal Tax Checklist"
        lead="Preparation is calmer when the year’s papers are gathered as you go. Print this page, keep it somewhere visible, and check things off."
        image="/img/photo-financial-partner.webp"
        imageAlt="A person reviewing finances on a laptop in warm window light"
      />

      <section className="py-24 lg:py-32" data-testid="personal-checklist-section">
        <div className="container-shell max-w-4xl">
          <Reveal>
            <button onClick={() => window.print()} data-testid="personal-checklist-print-button" className="btn-primary no-print">
              <Printer className="h-4 w-4" /> Print this checklist
            </button>
          </Reveal>
          <div className="mt-14 space-y-14">
            {SECTIONS.map((s, si) => (
              <Reveal key={s.title} delay={si * 0.06}>
                <h2 className="font-serif text-2xl text-olive sm:text-3xl">{s.title}</h2>
                <ul className="mt-6 space-y-3.5">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 border-b border-line pb-3.5 text-base leading-relaxed text-moss" data-testid={`personal-checklist-item-${s.items.indexOf(item) + 1}`}>
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-olive/50" aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 rounded-md border border-olive/25 bg-sage p-8 no-print">
            <p className="text-sm leading-relaxed text-moss"><span className="font-bold text-ink">General guidance, not tax advice.</span> Please never send tax documents, Social Security numbers, or account numbers through this website. Sensitive information is exchanged through secure channels once we’re working together.</p>
          </Reveal>
        </div>
      </section>

      <CtaSection heading="Want a hand with the gathering?" body="Book a consultation and we’ll talk through where things stand. No judgment, just a plan." />
    </>
  );
}
